import {
  WORKFLOW_STAGES,
  SUPPLY_ONLY_WORKFLOW_STAGES,
  PROGRESS_STAGES,
  SUPPLY_ONLY_PROGRESS_STAGES,
  getStageFromTasks,
  type WorkflowTask,
  type WorkflowStage,
} from '../components/ProjectWorkflow';

export const DEPOSIT_RECEIVED_TASK = 'Deposit received';
export const PRODUCTION_DATE_TASK = 'Production Date';

export type WorkflowProjectHints = {
  deposit_received?: boolean | null;
  ordered?: boolean | null;
  complete?: boolean | null;
  project_status?: 'active' | 'completed' | 'cancelled' | null;
};

const LATER_THAN_DEPOSIT_TASKS = [
  'Order Goods',
  'Production Date',
  'Schedule Installation Date',
  'Schedule Delivery Date',
  'Installation Complete',
  'Delivery Complete',
  'Request payment',
  'Receive payment',
  'Send Receipt',
  'Request Review',
  'Complete Project',
] as const;

export const LIFECYCLE_TANK_LABELS: Record<string, string> = {
  New: 'New',
  'Supplier Quote Received': 'Supplier quote',
  'Deposit Received': 'Deposit',
  Ordered: 'Ordered',
  'Installation Scheduled': 'Installation',
  'Delivery Scheduled': 'Delivery',
  Completed: 'Complete',
  Cancelled: 'Cancelled',
};

export const LIFECYCLE_TANK_BANDS: Record<string, 'pre' | 'imp' | 'post'> = {
  New: 'pre',
  'Supplier Quote Received': 'pre',
  'Deposit Received': 'pre',
  Ordered: 'pre',
  'Installation Scheduled': 'imp',
  'Delivery Scheduled': 'imp',
  Completed: 'post',
  Cancelled: 'post',
};

export function getWorkflowStageMap(supplyOnly: boolean) {
  return supplyOnly ? SUPPLY_ONLY_WORKFLOW_STAGES : WORKFLOW_STAGES;
}

export function getWorkflowProgressStages(supplyOnly: boolean): readonly WorkflowStage[] {
  return supplyOnly ? SUPPLY_ONLY_PROGRESS_STAGES : PROGRESS_STAGES;
}

export function shouldBackfillDepositReceived(
  existingTasks: WorkflowTask[],
  project?: WorkflowProjectHints
): boolean {
  const alreadyPresent = existingTasks.some((task) => task.task === DEPOSIT_RECEIVED_TASK);
  if (alreadyPresent) return false;
  if (project?.deposit_received || project?.ordered || project?.complete) return true;
  if (project?.project_status === 'completed') return true;
  return existingTasks.some(
    (task) => task.completed && (LATER_THAN_DEPOSIT_TASKS as readonly string[]).includes(task.task)
  );
}

function hydrateTasks(
  existingTasks: WorkflowTask[],
  allTaskNames: readonly string[],
  project?: WorkflowProjectHints
): WorkflowTask[] {
  const backfillDeposit = shouldBackfillDepositReceived(existingTasks, project);
  const hydrated = allTaskNames.map((taskName) => {
    const existing = existingTasks.find((task) => task.task === taskName)
      || (taskName === 'Final Survey Complete'
        ? existingTasks.find((task) => task.task === 'Second Survey Complete')
        : undefined);
    if (existing) return { ...existing, task: taskName };
    return {
      task: taskName,
      completed: taskName === DEPOSIT_RECEIVED_TASK && backfillDeposit,
      productionDates: undefined,
    };
  });
  const production = existingTasks.find((task) => task.task === PRODUCTION_DATE_TASK);
  return [
    ...hydrated.filter((task) => task.task !== PRODUCTION_DATE_TASK),
    production
      ? { ...production, task: PRODUCTION_DATE_TASK }
      : { task: PRODUCTION_DATE_TASK, completed: false, productionDates: [] },
  ];
}

export function getFullWorkflowTasks(
  existingTasks: WorkflowTask[],
  project?: WorkflowProjectHints
): WorkflowTask[] {
  const allTasks = PROGRESS_STAGES.flatMap((stage) => WORKFLOW_STAGES[stage] || []);
  return hydrateTasks(existingTasks, allTasks, project);
}

export function getFullWorkflowTasksForMode(
  existingTasks: WorkflowTask[],
  supplyOnly: boolean,
  project?: WorkflowProjectHints
): WorkflowTask[] {
  const stages = getWorkflowProgressStages(supplyOnly);
  const workflowStages = getWorkflowStageMap(supplyOnly);
  const allTasks = stages.flatMap((stage) => workflowStages[stage]);
  return hydrateTasks(existingTasks, allTasks, project);
}

export function applyWorkflowTaskChange(
  tasks: WorkflowTask[],
  taskName: string,
  completed: boolean
): WorkflowTask[] {
  let next = tasks.map((task) => (task.task === taskName ? { ...task, completed } : task));

  if (taskName === 'Cancel Project' && completed) {
    next = next.map((task) => (task.task === 'Complete Project' ? { ...task, completed: false } : task));
  }
  if (taskName === 'Complete Project' && completed) {
    next = next.map((task) => (task.task === 'Cancel Project' ? { ...task, completed: false } : task));
  }

  return next;
}

export type WorkspaceLifecycleLabel = 'Active' | 'Completed' | 'Cancelled';

export function workspaceLifecycleStatus(project: {
  project_status?: 'active' | 'completed' | 'cancelled' | null;
  status?: string | null;
  complete?: boolean | null;
  workflow_tasks?: Array<{ task: string; completed?: boolean }> | null;
}): WorkspaceLifecycleLabel {
  const tasks = Array.isArray(project.workflow_tasks) ? project.workflow_tasks : [];
  const cancelled =
    project.project_status === 'cancelled'
    || project.status === 'cancelled'
    || tasks.some((task) => task.task === 'Cancel Project' && task.completed);
  if (cancelled) return 'Cancelled';

  const completed =
    project.project_status === 'completed'
    || project.complete === true
    || project.status === 'completed'
    || tasks.some((task) => task.task === 'Complete Project' && task.completed);
  if (completed) return 'Completed';

  return 'Active';
}

export function matchesWorkspaceStatusFilter(
  project: {
    project_status?: 'active' | 'completed' | 'cancelled' | null;
    status?: string | null;
    complete?: boolean | null;
    workflow_tasks?: Array<{ task: string; completed?: boolean }> | null;
  },
  statusFilter: 'all' | 'active' | 'completed' | 'cancelled'
): boolean {
  if (statusFilter === 'all') return true;
  return workspaceLifecycleStatus(project).toLowerCase() === statusFilter;
}

export function getWorkflowFlagPatch(
  tasks: WorkflowTask[],
  supplyOnly: boolean,
  project?: WorkflowProjectHints
): {
  supplier_quote_received: boolean;
  deposit_received: boolean;
  ordered: boolean;
  complete: boolean;
  project_status: 'active' | 'completed' | 'cancelled';
} {
  const completed = new Set(tasks.filter((task) => task.completed).map((task) => task.task));
  const cancelled = completed.has('Cancel Project');
  const complete = completed.has('Complete Project') && !cancelled;
  const project_status: 'active' | 'completed' | 'cancelled' = cancelled
    ? 'cancelled'
    : complete
      ? 'completed'
      : 'active';

  const stage = getStageFromTasks(
    tasks,
    {
      complete,
      project_status,
      supply_only: supplyOnly,
    },
    supplyOnly
  );

  const pastDeposit =
    stage === 'Ordered' ||
    stage === 'Installation Scheduled' ||
    stage === 'Delivery Scheduled' ||
    stage === 'Completed';

  return {
    supplier_quote_received:
      stage === 'Supplier Quote Received' ||
      stage === 'Deposit Received' ||
      pastDeposit,
    deposit_received: completed.has(DEPOSIT_RECEIVED_TASK) || pastDeposit,
    ordered: stage === 'Ordered' || stage === 'Installation Scheduled' || stage === 'Delivery Scheduled' || complete,
    complete,
    project_status,
  };
}

export function getTankFillTasks(
  stage: WorkflowStage,
  supplyOnly: boolean
): readonly string[] {
  const names = getWorkflowStageMap(supplyOnly)[stage] || [];
  if (stage === 'Completed') {
    return names.filter((name) => name !== 'Cancel Project');
  }
  return names.filter((name) => name !== PRODUCTION_DATE_TASK);
}
